import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, Eye, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import api, { formatCurrency, formatDate, statusLabels } from '../lib/api';

const MyOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadOrders = async () => {
      try {
        const response = await api.get('/orders');
        setOrders(response.data);
      } catch (error) {
        console.error('Failed to load orders:', error);
      } finally {
        setLoading(false);
      }
    };
    loadOrders();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'paid':
      case 'approved': return 'status-paid';
      case 'preparing': return 'status-preparing';
      case 'shipping': return 'status-shipping';
      case 'delivered': return 'status-delivered';
      case 'cancelled': return 'status-cancelled';
      default: return 'status-pending';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        <h1 className="font-heading text-2xl font-bold text-foreground mb-6" data-testid="my-orders-title">
          Meus Pedidos
        </h1>

        {orders.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-24 h-24 mx-auto mb-6 bg-slate-100 rounded-full flex items-center justify-center">
              <Package className="w-12 h-12 text-slate-400" />
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground mb-2">
              Nenhum pedido encontrado
            </h2>
            <p className="text-muted-foreground mb-8">
              Você ainda não fez nenhum pedido
            </p>
            <Link to="/">
              <Button size="lg" className="rounded-full">
                Começar a Comprar
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <Link
                key={order.id}
                to={`/pedido/${order.id}`}
                className="block bg-white rounded-2xl p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`order-card-${order.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-heading font-bold text-foreground">
                        Pedido #{order.order_number}
                      </h3>
                      <span className={`status-badge ${getStatusColor(order.status)}`}>
                        {statusLabels[order.status]}
                      </span>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-2">
                      {formatDate(order.created_at)}
                    </p>
                    
                    <div className="flex flex-wrap gap-2 mb-3">
                      {order.items.slice(0, 3).map((item, index) => (
                        <span
                          key={index}
                          className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full"
                        >
                          {item.product_name}
                        </span>
                      ))}
                      {order.items.length > 3 && (
                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
                          +{order.items.length - 3} mais
                        </span>
                      )}
                    </div>
                    
                    <p className="font-bold text-lg text-primary">
                      {formatCurrency(order.total)}
                    </p>
                  </div>
                  
                  <Button variant="ghost" size="icon" className="flex-shrink-0">
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default MyOrdersPage;
